import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { SELLER_JOURNEY_STEPS } from '../components/BuyerJourney';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { toast } from 'sonner';
import {
  CheckCircle, Circle, ChevronDown, ChevronUp, Lightbulb,
  ArrowRight, Building2, Lock, ChevronRight
} from 'lucide-react';
import ProfessionalResourcesSection from '../components/ProfessionalResourcesSection';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const StepIndicator = ({ steps, current, onSelect, user }) => (
  <div className="flex items-center justify-center gap-1 sm:gap-2 mb-10 flex-wrap" data-testid="selling-step-indicator">
    {steps.map((s, i) => (
      <React.Fragment key={s.id}>
        <button
          onClick={() => {
            if (i > 0 && !user) {
              toast.error('Please login to access this step');
              return;
            }
            onSelect(i);
          }}
          className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 text-xs font-semibold tracking-wider uppercase transition-all ${
            i === current
              ? 'bg-[#E07A3A] text-white'
              : i < current
              ? 'bg-emerald-100 text-emerald-700'
              : i > 0 && !user
              ? 'bg-red-50 text-red-400 border border-red-200'
              : 'bg-slate-100 text-slate-400'
          }`}
          data-testid={`selling-step-nav-${i}`}
        >
          {i < current ? <CheckCircle className="h-3.5 w-3.5" /> : i > 0 && !user ? <Lock className="h-3.5 w-3.5" /> : <span>{i + 1}</span>}
          <span className="hidden sm:inline">{s.title.length > 18 ? s.title.slice(0, 18) + '...' : s.title}</span>
        </button>
        {i < steps.length - 1 && <ChevronRight className="h-4 w-4 text-slate-300 hidden sm:block" />}
      </React.Fragment>
    ))}
  </div>
);

const SellingGuidePage = () => {
  const { user } = useAuth();
  const { country } = useCountry();
  const { countryInfo, fees } = useCountry();
  const [currentStep, setCurrentStep] = useState(0);
  const [checkedItems, setCheckedItems] = useState({});
  const [showTip, setShowTip] = useState(null);
  const [loading, setLoading] = useState(true);

  const steps = SELLER_JOURNEY_STEPS[country] || SELLER_JOURNEY_STEPS.UK;
  const step = steps[currentStep];

  useEffect(() => {
    const loadProgress = async () => {
      if (!user) { setLoading(false); return; }
      try {
        const res = await axios.get(`${API_URL}/api/buyer-journey/progress`);
        if (res.data?.checked_items) setCheckedItems(res.data.checked_items);
      } catch (err) { logger.error('Load progress:', err); }
      setLoading(false);
    };
    loadProgress();
  }, [user]);

  const saveProgress = async (updated) => {
    if (!user) return;
    try {
      await axios.post(`${API_URL}/api/buyer-journey/progress`, { checked_items: updated, country });
    } catch (err) { logger.error('Save progress:', err); }
  };

  const toggleItem = (itemId) => {
    if (!user) {
      toast.error('Please sign in to track your progress');
      return;
    }
    const updated = { ...checkedItems, [itemId]: !checkedItems[itemId] };
    setCheckedItems(updated);
    saveProgress(updated);
  };

  const getStepProgress = (s) => {
    const checked = s.items.filter(i => checkedItems[i.id]).length;
    return { checked, total: s.items.length, percent: Math.round((checked / s.items.length) * 100) };
  };

  const totalChecked = steps.reduce((acc, s) => acc + s.items.filter(i => checkedItems[i.id]).length, 0);
  const totalItems = steps.reduce((acc, s) => acc + s.items.length, 0);
  const overallPercent = Math.round((totalChecked / totalItems) * 100);

  const countryLabel = country === 'IN' ? 'India' : country === 'CA' ? 'Canada' : 'United Kingdom';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="selling-guide-page">
      <Header />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Header */}
        <div className="text-center mb-8">
          <span className="text-xs tracking-[0.3em] text-[#E07A3A] mb-2 block uppercase font-semibold">
            Selling Journey &mdash; {countryLabel}
          </span>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl text-[#0F172A] font-semibold">
            Sell Your Property<span className="italic text-[#E07A3A]"> With Confidence</span>
          </h1>
          <p className="text-slate-500 mt-3 max-w-xl mx-auto text-sm">
            From valuation to handing over keys — follow every step to sell your property faster and for the best price.
          </p>
        </div>

        {/* Overall Progress */}
        {user && (
          <div className="max-w-md mx-auto mb-8" data-testid="selling-overall-progress">
            <div className="flex justify-between text-xs text-slate-400 mb-1">
              <span>{totalChecked} of {totalItems} tasks</span>
              <span className="font-semibold text-[#E07A3A]">{overallPercent}%</span>
            </div>
            <div className="h-2 bg-slate-100 overflow-hidden">
              <div className="h-full bg-[#E07A3A] transition-all duration-500" style={{ width: `${overallPercent}%` }} />
            </div>
          </div>
        )}

        {!user && (
          <div className="max-w-md mx-auto mb-8 p-4 bg-red-50 border border-red-200 text-center" data-testid="selling-login-prompt">
            <Lock className="h-5 w-5 text-red-500 mx-auto mb-2" />
            <p className="text-sm text-red-600 font-semibold">Please login to access full guide and track progress</p>
            <p className="text-xs text-red-400 mt-1">Step 1 is free to preview. Sign in to unlock all steps.</p>
            <Link to="/register">
              <Button className="mt-3 bg-red-500 text-white hover:bg-red-600 text-xs tracking-widest uppercase font-semibold px-6 py-4" data-testid="selling-guide-register-btn">
                Sign Up Free <ArrowRight className="ml-2 h-3.5 w-3.5" />
              </Button>
            </Link>
          </div>
        )}

        {/* Step Indicator */}
        <StepIndicator steps={steps} current={currentStep} onSelect={setCurrentStep} user={user} />

        {/* Current Step Content */}
        {step && (
          <div className="max-w-3xl mx-auto" data-testid={`selling-step-content-${currentStep}`}>
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#0F172A] text-white flex items-center justify-center mx-auto mb-4">
                {step.icon || <Building2 className="h-7 w-7" />}
              </div>
              <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#0F172A] font-semibold mb-2">{step.title}</h2>
              <p className="text-slate-500 text-sm max-w-lg mx-auto">{step.description}</p>
              {user && (() => {
                const p = getStepProgress(step);
                return (
                  <div className="mt-4 inline-flex items-center gap-2 px-4 py-1.5 bg-slate-50 text-xs text-slate-500 font-medium">
                    <span>{p.checked}/{p.total} complete</span>
                    <div className="w-20 h-1.5 bg-slate-200">
                      <div className={`h-full transition-all ${p.percent === 100 ? 'bg-emerald-500' : 'bg-[#E07A3A]'}`} style={{ width: `${p.percent}%` }} />
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Checklist Items */}
            <div className="space-y-3 mb-10">
              {step.items.map((item) => (
                <div key={item.id} className="border border-slate-100 hover:border-slate-200 transition-colors" data-testid={`selling-item-${item.id}`}>
                  <div className="flex items-start gap-3 p-4">
                    <button
                      onClick={() => toggleItem(item.id)}
                      className="flex-shrink-0 mt-0.5"
                      data-testid={`selling-check-${item.id}`}
                    >
                      {checkedItems[item.id] ? (
                        <CheckCircle className="h-5 w-5 text-emerald-500" />
                      ) : (
                        <Circle className="h-5 w-5 text-slate-300 hover:text-[#E07A3A] transition-colors" />
                      )}
                    </button>
                    <p className={`flex-1 text-sm leading-relaxed ${checkedItems[item.id] ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                      {item.label}
                    </p>
                    <button
                      onClick={() => setShowTip(showTip === item.id ? null : item.id)}
                      className={`flex-shrink-0 p-1 transition-colors ${showTip === item.id ? 'text-[#E07A3A]' : 'text-slate-300 hover:text-[#E07A3A]'}`}
                      data-testid={`selling-tip-${item.id}`}
                    >
                      <Lightbulb className="h-4 w-4" />
                    </button>
                  </div>
                  {showTip === item.id && (
                    <div className="mx-4 mb-4 p-3 bg-amber-50 border border-amber-100 text-sm text-amber-800 leading-relaxed" data-testid={`selling-tip-content-${item.id}`}>
                      <span className="font-semibold text-[#E07A3A] text-xs uppercase tracking-wider block mb-1">Shopperden Guidance</span>
                      {item.tip}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center pt-6 border-t border-slate-100">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                disabled={currentStep === 0}
                className="text-xs tracking-widest uppercase font-semibold border-slate-200"
                data-testid="selling-prev-btn"
              >
                Previous
              </Button>
              {currentStep < steps.length - 1 ? (
                <Button
                  onClick={() => {
                    if (currentStep >= 0 && !user) {
                      toast.error('Please login to access the next step');
                      return;
                    }
                    setCurrentStep(currentStep + 1);
                  }}
                  className="bg-[#E07A3A] text-white hover:bg-[#C4652E] text-xs tracking-widest uppercase font-semibold px-8 py-5"
                  data-testid="selling-next-btn"
                >
                  {!user && currentStep === 0 ? (
                    <><Lock className="mr-2 h-4 w-4" />Login to Continue</>
                  ) : (
                    <>Next Step <ArrowRight className="ml-2 h-4 w-4" /></>
                  )}
                </Button>
              ) : (
                <Link to="/register">
                  <Button
                    className="bg-[#0F172A] text-white hover:bg-[#1e293b] text-xs tracking-widest uppercase font-semibold px-8 py-5"
                    data-testid="selling-list-property-btn"
                  >
                    Create Digital Property Profile <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Professional directory deep-links */}
        <div className="mt-10">
          <ProfessionalResourcesSection context="selling" country={country || 'UK'} />
        </div>

        {/* Savings CTA */}
        <div className="mt-16 bg-[#0F172A] p-8 text-center" data-testid="selling-guide-cta">
          <Building2 className="h-10 w-10 text-[#E07A3A] mx-auto mb-4" />
          <h3 className="font-['Playfair_Display'] text-2xl text-white font-semibold mb-2">Your listing, your choice</h3>
          <p className="text-white/50 text-sm max-w-lg mx-auto mb-6">
            List direct on Shopperden for a flat fee, or work with a verified estate agent — both routes, one platform.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button className="bg-[#E07A3A] text-white hover:bg-[#C4652E] px-8 py-5 text-sm tracking-widest uppercase font-semibold" data-testid="selling-register-btn">
                List Property Free <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/buying-guide">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 px-8 py-5 text-sm tracking-widest uppercase font-semibold" data-testid="selling-buying-guide-btn">
                Buying Guide
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellingGuidePage;
